import { inject } from 'mobx-react';
import * as React from 'react';

import { ClickTracker } from 'bernie-analytics';
import { ILocalization, withLocalization } from 'bernie-l10n';
import { RouteStore } from 'bernie-core';

import { UitkIcon } from 'uitk-react-icons';
import { UitkLinkButton } from 'uitk-react-button';

import { DateUtils } from 'utils/date-utils';

const LinkButtonTracked = ClickTracker(UitkLinkButton);

const MAXIMUM_DATE_SPAN = 28;
const NUM_OF_DAYS_BOOKABLE_IN_ADVANCE = 500;

export interface StepperButtonProps {
  clickHandler?: (event: any, isTriggeredByStepper: boolean) => void;
  endDate: string;
  l10n?: ILocalization;
  linkName: string;
  loadingOffers: boolean;
  rfrr: string;
  routeStore?: RouteStore;
  startDate: string;
  stepperType: string;
}

export interface StepperButtonState {
  mounted: boolean;
}

@withLocalization
@inject('routeStore')
export class StepperButton extends React.Component<StepperButtonProps, StepperButtonState> {
  private dateUtils: DateUtils;

  constructor(props) {
    super(props);

    this.stepDates = this.stepDates.bind(this);

    this.state = {
      mounted: false,
    };

    this.dateUtils = new DateUtils(props.l10n);
  }

  public static defaultProps = {
    iconName: '',
    l10n: null,
  };

  public stepDates() {
    if (this.props.clickHandler && typeof this.props.clickHandler === 'function') {
      this.props.clickHandler(this.determineStepDate(), true);
    }
  }

  public determineIfDisabled() {
    return !this.state.mounted || !this.props.startDate || !this.props.endDate || !this.determineStepDate();
  }

  public determineStepDate() {
    const { dateUtils } = this;

    const { endDate, startDate, stepperType } = this.props;
    const currentCheckInDate = startDate;
    const currentCheckOutDate = endDate;
    const stepDateEvent = { target: [] };
    let stepCheckInDate;
    let stepCheckOutDate;
    switch (stepperType) {
      case 'CHECK_IN_MINUS':
        stepCheckInDate = dateUtils.getISODayBefore(currentCheckInDate);
        if (DateUtils.isBefore(stepCheckInDate, dateUtils.getToday())) {
          stepCheckInDate = undefined;
        } else if (dateUtils.getDaysBetween(stepCheckInDate, currentCheckOutDate) >= MAXIMUM_DATE_SPAN) {
          stepCheckInDate = undefined;
        }
        break;
      case 'CHECK_IN_PLUS':
        stepCheckInDate = dateUtils.getISODayAfter(currentCheckInDate);
        if (dateUtils.getDaysBetween(dateUtils.getToday(), stepCheckInDate) >= NUM_OF_DAYS_BOOKABLE_IN_ADVANCE) {
          stepCheckInDate = undefined;
        } else if (dateUtils.isSame(stepCheckInDate, currentCheckOutDate)) {
          stepCheckOutDate = dateUtils.getISODayAfter(currentCheckOutDate);
        } else if (dateUtils.getDaysBetween(currentCheckInDate, currentCheckOutDate) >= MAXIMUM_DATE_SPAN) {
          stepCheckInDate = dateUtils.getNDaysFrom(currentCheckOutDate, -(MAXIMUM_DATE_SPAN - 1));
        }
        break;
      case 'CHECK_OUT_MINUS':
        stepCheckOutDate = dateUtils.getISODayBefore(currentCheckOutDate);
        if (DateUtils.isBefore(stepCheckOutDate, dateUtils.getISODayAfter(dateUtils.getToday()))) {
          stepCheckOutDate = undefined;
        } else if (dateUtils.isSame(stepCheckOutDate, currentCheckInDate)) {
          stepCheckInDate = dateUtils.getISODayBefore(currentCheckInDate);
        } else if (dateUtils.getDaysBetween(currentCheckInDate, currentCheckOutDate) >= MAXIMUM_DATE_SPAN) {
          stepCheckOutDate = dateUtils.getNDaysFrom(currentCheckInDate, MAXIMUM_DATE_SPAN - 1);
        }
        break;
      case 'CHECK_OUT_PLUS':
        stepCheckOutDate = dateUtils.getISODayAfter(currentCheckOutDate);
        if (dateUtils.getDaysBetween(dateUtils.getToday(), stepCheckOutDate) > NUM_OF_DAYS_BOOKABLE_IN_ADVANCE) {
          stepCheckOutDate = undefined;
        } else if (dateUtils.getDaysBetween(currentCheckInDate, stepCheckOutDate) >= MAXIMUM_DATE_SPAN) {
          stepCheckOutDate = undefined;
        }
        break;
      default:
        stepCheckInDate = undefined;
        stepCheckOutDate = undefined;
        break;
    }

    if (!stepCheckInDate && !stepCheckOutDate) {
      return false;
    }

    if (stepCheckInDate) {
      stepDateEvent.target.push({
        name: 'startDate',
        value: stepCheckInDate,
      });
    }

    if (stepCheckOutDate) {
      stepDateEvent.target.push({
        name: 'endDate',
        value: stepCheckOutDate,
      });
    }

    return stepDateEvent;
  }

  public componentDidMount() {
    this.setState({
      mounted: true,
    });
  }

  public render() {
    const { l10n, linkName, loadingOffers, rfrr, stepperType } = this.props;
    let iconName;
    let identifier = 'date-stepper-button';
    let ariaLabelKey;
    switch (stepperType) {
      case 'CHECK_IN_MINUS':
        identifier += '-check-in-previous';
        iconName = 'chevron_left';
        ariaLabelKey = 'hotels.infosite.availabilityWizard.changeDate.checkIn.decrease.a11yLabel';
        break;
      case 'CHECK_OUT_MINUS':
        identifier += '-check-out-previous';
        iconName = 'chevron_left';
        ariaLabelKey = 'hotels.infosite.availabilityWizard.changeDate.checkOut.decrease.a11yLabel';
        break;
      case 'CHECK_IN_PLUS':
        identifier += '-check-in-next';
        iconName = 'chevron_right';
        ariaLabelKey = 'hotels.infosite.availabilityWizard.changeDate.checkIn.increase.a11yLabel';
        break;
      case 'CHECK_OUT_PLUS':
        identifier += '-check-out-next';
        iconName = 'chevron_right';
        ariaLabelKey = 'hotels.infosite.availabilityWizard.changeDate.checkOut.increase.a11yLabel';
        break;
      default:
        break;
    }

    return (
      <LinkButtonTracked
        data-stid={identifier}
        linkName={linkName}
        disabled={loadingOffers || this.determineIfDisabled()}
        onClick={this.stepDates}
        rfrr={rfrr}
        aria-label={l10n.formatText(ariaLabelKey)}
      >
        <span className="uitk-icon uitk-type-center">
          <UitkIcon name={iconName} />
        </span>
      </LinkButtonTracked>
    );
  }
}
